/**
 * RootEntry — quem decide o que a `/` serve.
 *
 * BUG-53: a PWA abre em `start_url: '/'` e esta rota servia a landing de
 * MARKETING para todo mundo, inclusive quem já estava logado — dois toques
 * ("começar leitura" → login) em toda abertura, todo dia, pra base inteira.
 *
 * O fix é aqui e NÃO no `start_url`: apontar o manifest direto pra `/home`
 * quebraria o visitante novo (cairia no ProtectedRoute → `/login`) e mudaria a
 * URL de entrada de quem chega pelo navegador. Quem decide é a sessão.
 *
 * `redirectPending` entra na espera junto com `isLoading` por causa do BUG-30:
 * na volta do login Google por redirect existe uma janela em que o usuário
 * ainda não materializou, e sem ele a landing piscaria bem no fim do login.
 *
 * BUG-93: sessão não basta pra decidir. Enquanto o beta é fechado, quem está
 * na FILA tem sessão e não tem acesso — e mandar essa pessoa pra `/home` a
 * entregava ao `BetaAccessGate`, que renderiza a tela de beta fechado. Como
 * `/login` e `/onboarding` também terminam lá, a `/` era a última porta aberta
 * e se fechou junto: só voltava à landing quem deslogasse. Por isso a raiz
 * consulta o mesmo veredito do porteiro (`betaVerdict.ts`) antes de redirecionar.
 *
 * A espera pelo veredito não custa um round-trip novo: é a MESMA chamada, uma
 * por carga de app, que o `/home` pagaria um render depois. O spinner só muda
 * de lugar.
 *
 * Arquivo próprio (e não uma função dentro do `AppRoutes`) porque esta decisão
 * tem três entradas e três saídas: testá-la de dentro do `AppRoutes` obrigaria
 * a montar a árvore de rotas inteira pra exercitar um `if`.
 */

import { lazy, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useBetaVerdict } from "./betaVerdict";
import { SessionBootScreen } from "../components/boot/SessionBootScreen";
import { prefetchHomeChunk } from "./routePrefetch";

const Landing = lazy(() => import("../pages/Landing"));

export function RootEntry() {
  const { user, isAnonymous, isLoading, redirectPending } = useAuth();
  const betaVerdict = useBetaVerdict();

  // Há sessão → o destino é a Home. O chunk dela começa a baixar AGORA, em
  // paralelo com a leitura do perfil, em vez de só depois que o boot assentar
  // (17/09: eram duas esperas em série — ver `routePrefetch.ts`).
  const hasSession = !!user && !isAnonymous;
  useEffect(() => {
    if (hasSession) prefetchHomeChunk();
  }, [hasSession]);

  // Sem decisão ainda: mostrar a tela de abertura com a marca, nunca a landing.
  // Um flash de página de venda pra quem já é usuário é pior que meio segundo
  // de espera.
  //
  // ⚠️ Esta é a tela do bug do build 22: o app nativo abre em `/` (é o
  // `start_url`), então É AQUI que a espera de sessão aparece — e era um disco
  // girando sem teto e sem nada a reportar. Agora ela tem marca, teto de 10s
  // por etapa, código de falha e o painel de log no toque longo.
  if (isLoading || redirectPending) return <SessionBootScreen />;

  // F1.5 do Roadmap PRO (11/09): visitante que chegou aqui com a sessão
  // anônima de um perfil público (ex.: voltou pra `/`) continua vendo a
  // landing — sem isto ele cairia no `/home` e batia direto no OnboardingGuard.
  if (user && !isAnonymous) {
    if (betaVerdict === "pending") return <SessionBootScreen />;
    // Onboarding incompleto não é tratado aqui — `/home` é `Guarded`, e o
    // OnboardingGuard redireciona pra `/onboarding`. Uma autoridade só.
    if (betaVerdict === "allowed") return <Navigate to="/home" replace />;
    // Bloqueado cai na landing: é a única superfície que explica o produto pra
    // quem ainda não entrou, e agora ela é alcançável sem logout.
  }

  return <Landing />;
}
